import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Button, Space} from 'antd';
import {useLocation, useNavigate} from 'react-router-dom';
import {connect} from 'react-redux';
import {ExclamationCircleFilled, PlusOutlined} from '@ant-design/icons';
import PropTypes from 'prop-types';
import {
  StyledInvoiceTabsContainer,
  StyledInvoicesTabsContent,
  StyledInvoicesTabsEmpty
} from './StyledInvoicesTabs';
import {StyledTableFiltersDivider} from '../../TransactionsPage/TableFilters/StyledTableFilters';
import UploadInvoicesModal from '../UploadInvoicesModal';
import MultipleSelectionBar from '../../../MultipleSelectionBar';
import Empty from '../../../Empty';
import Tabs from '../../../Tabs';
import UnmatchedTotals from '../tabComponents/UnmatchedTotals';
import InvoicesTab from '../tabs/InvoicesTab';
import ExportModal from '../../TransactionsPage/ExportModal';
import {
  DEFAULT_MODAL_PROPS,
  DEFAULT_PAGE_SIZE,
  DEFAULT_TABLE_DATA,
  invoiceStatusConstants
} from '../../../../constants';
import {invoicesActions} from '../../../../state/actions/invoice.actions';
import {
  elementHelpers,
  locationHelpers,
  systemHelpers,
  tabHelpers,
} from '../../../../utils/helpers';
import {firebaseEvents} from '../../../../snippets/firebase';
import {useHasAccess} from '../../../../hooks';
import {ReactComponent as EmptyUnmatchedSvgImage} from '../../../../static/images/pages/invoices/empty-umnatched.svg';
import routes from '../../../../routes/routes.json';


const {MATCHED: MATCHED_STATUS} = invoiceStatusConstants;

const tabKeys = {
  ALL: 'ALL',
  OUTSTANDING: 'OUTSTANDING',
  SETTLED: 'SETTLED',
};

const {ALL, OUTSTANDING, SETTLED} = tabKeys;

const defaultQuery = {
  per_page: DEFAULT_PAGE_SIZE,
  sort_by: 'date',
  sort_order: 'desc'
}

const defaultViewKey = tabKeys.OUTSTANDING;
const defaultTabKey = defaultViewKey.toLowerCase();

const {logEvent} = systemHelpers;

const InvoicesTabs = ({
  dispatch,
  batchCreateInvoices,
  batchDeleteInvoices,
  getInvoices,
  downloadInvoice,
  downloadZipInvoices,
  saveInvoicesListUrl,
  updateInvoice,
  handleUpdateTotal,

  total,
  totalLoading = false,
  unmatchedTotal,
  ...rest
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [t] = useTranslation(['main', 'invoices']);
  const [activeTabKey, setActiveTabKey] = useState(locationHelpers.getHash(location) || defaultTabKey);
  const [tableData, setTableData] = useState({
    [ALL]: DEFAULT_TABLE_DATA,
    [OUTSTANDING]: DEFAULT_TABLE_DATA,
    [SETTLED]: DEFAULT_TABLE_DATA
  });
  const [queries, setQueries] = useState({
    [ALL]: defaultQuery,
    [OUTSTANDING]: defaultQuery,
    [SETTLED]: defaultQuery
  });
  const [loading, setLoading] = useState({
    [ALL]: false,
    [OUTSTANDING]: false,
    [SETTLED]: false
  });
  const [rowsSelected, setRowsSelected] = useState({
    [ALL]: [],
    [OUTSTANDING]: [],
    [SETTLED]: []
  });
  const [requiredUpdate, setRequiredUpdate] = useState({
    [ALL]: true,
    [OUTSTANDING]: true,
    [SETTLED]: true
  });
  const [uploadInvoicesModalProps, setUploadInvoicesModalProps] = useState(DEFAULT_MODAL_PROPS);
  const [exportModalProps, setExportModalProps] = useState(DEFAULT_MODAL_PROPS);

  const hasCreateAccess = useHasAccess('POST_INVOICES_BATCH');
  const hasEditAccess = useHasAccess('PUT_INVOICE_LINK_EXPENSE');

  const trans = (key) => t(`invoices:${key}`);

  const emptyT = (key) => trans(`empty.${key}`);

  const unmatchedEmptyT = (key) => trans(`unmatchedEmpty.${key}`);

  const handleOnAddClick = () => setUploadInvoicesModalProps({...uploadInvoicesModalProps, open: true});

  const handleOnExportClick = () => setExportModalProps({...exportModalProps, open: true});

  const unmatchedEmptyScreen = (
    <StyledInvoicesTabsEmpty
      image={<EmptyUnmatchedSvgImage />}
      description={(
        <>
          {unmatchedEmptyT('description1')}<br/>
          {unmatchedEmptyT('description2')}<br/>
          {unmatchedEmptyT('description3')}
        </>
      )}
      title={unmatchedEmptyT('title')}
    >
      <Button
        onClick={handleOnAddClick}
        size='large'
        type='primary'
      >
        {unmatchedEmptyT('button')}
      </Button>
    </StyledInvoicesTabsEmpty>
  );

  const filtersLeftSideContent = (
    <UnmatchedTotals
      className='unmatched-totals'
      data={unmatchedTotal}
      loading={unmatchedTotal?.loading}
    />
  );

  const filtersRightSideContent = (
    <Space size={0}>
      <Button
        onClick={handleOnExportClick}
        type='default'
        size='large'
      >
        {t('export')}
      </Button>
      {hasCreateAccess && (
        <>
          <StyledTableFiltersDivider type='vertical' />
          <Button
            icon={<PlusOutlined />}
            onClick={handleOnAddClick}
            type='primary'
            size='large'
          >
            {t('invoice')}
          </Button>
        </>
      )}
    </Space>
  );

  const isExistsInvoices = useMemo(() => total && total.all > 0, [total]);

  useEffect(() => {
    loadData({
      initial: true,
      view: tabKeys[activeTabKey.toUpperCase()]
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    logEvent(firebaseEvents.INVOICES_ACTIVE_TAB, {name: activeTabKey});
  }, [activeTabKey]);

  const getLabel = (props) => tabHelpers.getLabel({...props, total, t: trans});

  const getQuery = (query, view) => {
    let additionalQuery = {};
    if (view === SETTLED) {
      additionalQuery = {...additionalQuery, status_eq: MATCHED_STATUS}
    } else if (view === OUTSTANDING) {
      additionalQuery = {...additionalQuery, status_not_eq: MATCHED_STATUS}
    }
    return {
      ...query,
      ...additionalQuery
    }
  }

  const loadData = ({view, query = null, initial = false, updateRequired = true}) => {
    const viewQuery = tabHelpers.getViewQuery({initial, query, queries, view});
    setQueries({
      ...queries,
      [view]: viewQuery
    });

    const finishTableLoading = () => {
      setLoading({...loading, [view]: false});
      updateRequired && setRequiredUpdate({...requiredUpdate, [view]: false});
    }

    setLoading({...loading, [view]: true});

    const queryData = getQuery(viewQuery, view);

    getInvoices(
      queryData,
      (response) => {
        tabHelpers.handleStoreTableData({tableData, setTableData, view, response, initial, rowsKey: 'invoices'});
        finishTableLoading();
      },
      finishTableLoading
    );
  }

  const updateTotal = () => handleUpdateTotal && handleUpdateTotal();

  const handleOnPaginate = (view, activePage) =>
    tabHelpers.handleOnPaginate({view, activePage, tableData, setTableData, loadDataFunc: loadData})

  const handleOnFilter = (view, filterQuery) =>
    tabHelpers.handleOnFilter({view, filterQuery, loadDataFunc: loadData, queries, setQueries});

  const handleOnTableChange = (view, pagination, filters, sorter, extra) => {
    const {action} = extra;
    if (action === 'sort') {
      const {order, field} = sorter;
      loadData({
        initial: true,
        view,
        query: {
          sort_by: field,
          sort_order: order === 'descend' ? 'desc' : 'asc'
        }
      });
    }
  }

  const handleOnRowsSelect = (view, value) => {
    setRowsSelected({
      ...rowsSelected,
      [view]: value
    });
  }

  const getTableDataValue = (view, key, defaultValue) => tableData.hasOwnProperty(view) ? tableData[view][key] : defaultValue;

  const getTabProps = ({key, totalKey, empty}) => {
    const data = getTableDataValue(key, 'rows', []);
    return {
      data,
      edit: hasEditAccess,
      empty: isExistsInvoices ? empty : emptyScreen,
      tableFiltersProps: {
        leftSideContent: key === OUTSTANDING ? filtersLeftSideContent : undefined,
        rightSideContent: filtersRightSideContent
      },
      loading: loading[key] || false,
      onFilter: (value) => handleOnFilter(key, value),
      tableProps: {
        onChange: (pagination, filters, sorter, extra) => handleOnTableChange(key, pagination, filters, sorter, extra),
        onRow: (record, index) => ({
          onClick: (e) => handleOnRowClick(e, record, index)
        }),
        pagination: tabHelpers.getTablePagination({handleOnPaginate, tableData, total, totalKey, view: key}),
        rowSelection: {
          selectedRowKeys: rowsSelected[key],
          onChange: (rows) => handleOnRowsSelect(key, rows)
        }
      }
    }
  }

  const emptyScreen = (
    <Empty
      description={emptyT('description')}
      title={emptyT('title')}
    >
      <Button
        onClick={handleOnAddClick}
        size='large'
        type='primary'
      >
        {trans('uploadInvoice')}
      </Button>
    </Empty>
  );

  const getTabs = () => {
    return [
      {
        key: 'outstanding',
        label: getLabel({key: 'outstanding', totalKey: 'unmatched'}),
        children: (
          <InvoicesTab
            {...getTabProps({key: OUTSTANDING, totalKey: 'unmatched', empty: unmatchedEmptyScreen})}
            unmatched={true}
          />
        )
      },
      {
        key: 'settled',
        label: getLabel({key: 'settled', totalKey: 'matched'}),
        children: (
          <InvoicesTab {...getTabProps({key: SETTLED, totalKey: 'matched'})} />
        )
      },
      {
        key: 'all',
        label: getLabel({key: 'all'}),
        children: (
          <InvoicesTab {...getTabProps({key: ALL, totalKey: 'all'})} />
        )
      },
    ]
  }

  const onChangeTab = (key) => {
    const view = tabKeys[key.toUpperCase()];
    updateTab(key);

    if (!getTableDataValue(view, 'loaded', false) || requiredUpdate[view]) loadData({view, initial: true});
  }

  const clearUpdateState = (view) => {
    setRequiredUpdate({
      ...requiredUpdate,
      [ALL]: true,
      [OUTSTANDING]: true,
      [SETTLED]: true,
      [view]: false
    });
  }

  const handleCancelUploadInvoicesModal = () => setUploadInvoicesModalProps({...uploadInvoicesModalProps, ...DEFAULT_MODAL_PROPS});

  const handleOkUploadInvoicesModal = (invoices, successCallback, errorCallback) => {
    setUploadInvoicesModalProps({...uploadInvoicesModalProps, loading: true});
    batchCreateInvoices(
      {invoices},
      () => {
        updateTab(defaultTabKey);
        loadData({view: defaultViewKey, initial: true, updateRequired: false});
        updateTotal();
        clearUpdateState(defaultViewKey);
        handleCancelUploadInvoicesModal();
        logEvent(firebaseEvents.INVOICES_ADD_NEW);
      },
      (resp) => {
        errorCallback && errorCallback(resp);
        setUploadInvoicesModalProps({...uploadInvoicesModalProps, loading: false});
      }
    )
  }

  const updateTab = (key) => {
    navigate(`${location.pathname}#${key}`);
    setActiveTabKey(key);
  }

  const getSelectedItem = () => Object.keys(rowsSelected).find(key => rowsSelected[key].length > 0) || undefined;

  const handleCancelSelected = () => {
    const selectedRows = {};
    Object.keys(rowsSelected).forEach(key => {
      selectedRows[key] = [];
    });
    setRowsSelected(selectedRows);
  }

  const handleDeleteSelected = () => {
    elementHelpers.showConfirmModal({
      content: trans('modal.deleteInvoices.description'),
      icon: <ExclamationCircleFilled />,
      okType: 'danger',
      onOk: () => {
        const item = getSelectedItem();
        if (item) {
          setLoading({...loading, [item]: true});
          batchDeleteInvoices(
            rowsSelected[item],
            () => {
              handleCancelSelected();
              loadData({view: item, initial: true});
              clearUpdateState(item);
              updateTotal();
              logEvent(firebaseEvents.INVOICES_DELETE_INVOICE);
            },
            () => setLoading({...loading, [item]: false})
          )
        } else {
          handleCancelSelected();
        }
      },
      title: trans('modal.deleteInvoices.title'),
      translate: t
    });
  }

  const handleOnRowClick = (e, record) => {
    const url = location.pathname + location.hash + location.search;
    const view = activeTabKey.toUpperCase();
    const query = getQuery(queries[view], view);

    saveInvoicesListUrl(url, query);
    navigate(`${routes.invoicesList}/${record.id}`);
  }

  const handleCancelExportModal = () => setExportModalProps({...exportModalProps, open: false, loading: false, error: null});

  const handleOkExport = (data) => {
    const dateRange = data.date_range;
    if (!dateRange || dateRange.length !== 2) return;

    const timeFormat = 'YYYY-MM-DD';
    const query = {
      date_gte: dateRange[0].format(timeFormat),
      date_lte: dateRange[1].format(timeFormat),
    };
    setExportModalProps({...exportModalProps, loading: true});
    downloadZipInvoices({
      query,
      successCallback: (data) => {
        window.open(data.url, 'download');
        handleCancelExportModal();
        logEvent(firebaseEvents.INVOICES_EXPORT);
      },
      errorCallback: () => setExportModalProps({
        ...exportModalProps,
        open: true,
        loading: false,
        error: t('fileIsTooLargeToDownloadMessage')
      })
    })
  }

  const tabs = getTabs();

  return (
    <>
      <StyledInvoiceTabsContainer>
        <StyledInvoicesTabsContent>
          <MultipleSelectionBar
            actions={[
              {onClick: handleDeleteSelected, label: t('delete')},
            ]}
            onCancel={setRowsSelected}
          />
          <Tabs
            {...rest}
            activeKey={activeTabKey}
            items={tabs}
            onChange={onChangeTab}
            type='card'
          />
        </StyledInvoicesTabsContent>
      </StyledInvoiceTabsContainer>

      <UploadInvoicesModal
        {...uploadInvoicesModalProps}
        onCancel={handleCancelUploadInvoicesModal}
        onOk={handleOkUploadInvoicesModal}
      />

      <ExportModal
        {...exportModalProps}
        enableFormatField={false}
        handleCancel={handleCancelExportModal}
        handleOk={handleOkExport}
        title={`${t('export')} ${t('invoices')}`}
      />

    </>
  );
}

InvoicesTabs.propTypes = {
  handleUpdateTotal: PropTypes.func,
  total: PropTypes.shape({
    all: PropTypes.number,
    matched: PropTypes.number,
    unmatched: PropTypes.number,
  }),
  unmatchedTotal: PropTypes.shape({
    due: PropTypes.number,
    loading: PropTypes.bool,
    overdue: PropTypes.number,
    upcoming: PropTypes.number,
  }),
}

const mapDispatchToProps = {
  getInvoices: invoicesActions.getInvoices,
  batchCreateInvoices: invoicesActions.batchCreateInvoices,
  batchDeleteInvoices: invoicesActions.batchDeleteInvoices,
  downloadInvoice: invoicesActions.downloadInvoice,
  downloadZipInvoices: invoicesActions.downloadZipInvoices,
  saveInvoicesListUrl: invoicesActions.saveInvoicesListUrl,
  updateInvoice: invoicesActions.updateInvoice,
}

export default connect(null, mapDispatchToProps)(InvoicesTabs);
